import { useState, useCallback, useRef, useEffect, memo } from 'react'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { X, Trash2, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

const CANVAS_ACTIVE_A = '#6366f1'
const CANVAS_ACTIVE_B = '#a78bfa'
const CANVAS_ACTIVE_ACCENT = '#818cf8'
const CANVAS_INACTIVE_A = '#525252'
const CANVAS_INACTIVE_B = '#737373'
const CANVAS_INACTIVE_ACCENT = '#a3a3a3'
const CANVAS_BURN_ACTIVE = '#f59e0b'
const CANVAS_BURN_INACTIVE = '#a3a3a3'

export const VALID_TRANSITION_TYPES = [
  'cut',
  'crossfade',
  'dip_to_black',
  'dip_to_white',
  'wipe_left',
  'wipe_right',
  'gl_fadegrayscale',
  'gl_luma',
  'gl_Dreamy',
  'gl_cube',
  'gl_doorway',
  'gl_pixelize',
  'gl_hexagonalize',
  'gl_squareswire',
  'gl_windowslice',
  'gl_windowblinds',
  'gl_Swirl',
  'gl_ripple',
  'gl_perlin',
  'gl_morph',
  'gl_WaterDrop',
  'gl_directionalwipe',
  'gl_angular',
  'gl_Radial',
  'gl_BowTieVertical',
  'gl_BowTieHorizontal',
  'gl_GlitchMemories',
  'gl_burn',
  'gl_kaleidoscope',
  'gl_flyeye',
  'gl_colorphase',
  'gl_crosswarp',
  'gl_DreamyZoom',
  'gl_CircleCrop',
  'gl_wind',
  'gl_luminance_melt',
] as const

const TRANSITION_CATEGORIES = [
  {
    label: 'Basic',
    transitions: [
      { type: 'crossfade', name: 'Crossfade' },
      { type: 'dip_to_black', name: 'Dip to Black' },
      { type: 'dip_to_white', name: 'Dip to White' },
      { type: 'wipe_left', name: 'Wipe Left' },
      { type: 'wipe_right', name: 'Wipe Right' },
    ],
  },
  {
    label: 'Cinematic',
    transitions: [
      { type: 'gl_fadegrayscale', name: 'Fade Grayscale' },
      { type: 'gl_luma', name: 'Luma' },
      { type: 'gl_Dreamy', name: 'Dreamy' },
    ],
  },
  {
    label: '3D',
    transitions: [
      { type: 'gl_cube', name: 'Cube' },
      { type: 'gl_doorway', name: 'Doorway' },
    ],
  },
  {
    label: 'Geometric',
    transitions: [
      { type: 'gl_pixelize', name: 'Pixelize' },
      { type: 'gl_hexagonalize', name: 'Hexagonalize' },
      { type: 'gl_squareswire', name: 'Squares Wire' },
      { type: 'gl_windowslice', name: 'Window Slice' },
      { type: 'gl_windowblinds', name: 'Window Blinds' },
    ],
  },
  {
    label: 'Organic',
    transitions: [
      { type: 'gl_Swirl', name: 'Swirl' },
      { type: 'gl_ripple', name: 'Ripple' },
      { type: 'gl_perlin', name: 'Perlin Noise' },
      { type: 'gl_morph', name: 'Morph' },
      { type: 'gl_WaterDrop', name: 'Water Drop' },
    ],
  },
  {
    label: 'Directional',
    transitions: [
      { type: 'gl_directionalwipe', name: 'Directional Wipe' },
      { type: 'gl_angular', name: 'Angular' },
      { type: 'gl_Radial', name: 'Radial' },
      { type: 'gl_BowTieVertical', name: 'Bow Tie V' },
      { type: 'gl_BowTieHorizontal', name: 'Bow Tie H' },
    ],
  },
  {
    label: 'Creative',
    transitions: [
      { type: 'gl_GlitchMemories', name: 'Glitch' },
      { type: 'gl_burn', name: 'Burn' },
      { type: 'gl_kaleidoscope', name: 'Kaleidoscope' },
      { type: 'gl_flyeye', name: 'Fly Eye' },
      { type: 'gl_colorphase', name: 'Color Phase' },
    ],
  },
  {
    label: 'Motion',
    transitions: [
      { type: 'gl_crosswarp', name: 'Cross Warp' },
      { type: 'gl_DreamyZoom', name: 'Dreamy Zoom' },
      { type: 'gl_CircleCrop', name: 'Circle Crop' },
      { type: 'gl_wind', name: 'Wind' },
      { type: 'gl_luminance_melt', name: 'Luminance Melt' },
    ],
  },
] as const

type TransitionType = (typeof VALID_TRANSITION_TYPES)[number]

function drawTransitionFrame(ctx: CanvasRenderingContext2D, type: string, progress: number, w: number, h: number, isActive: boolean) {
  ctx.clearRect(0, 0, w, h)
  const colorA = isActive ? CANVAS_ACTIVE_A : CANVAS_INACTIVE_A
  const colorB = isActive ? CANVAS_ACTIVE_B : CANVAS_INACTIVE_B
  const accent = isActive ? CANVAS_ACTIVE_ACCENT : CANVAS_INACTIVE_ACCENT

  switch (type) {
    case 'crossfade': {
      ctx.globalAlpha = 1 - progress
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = progress
      ctx.fillStyle = colorB
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 1
      break
    }
    case 'dip_to_black':
    case 'dip_to_white': {
      const mid = progress < 0.5 ? progress * 2 : 2 - progress * 2
      ctx.fillStyle = progress < 0.5 ? colorA : colorB
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = type === 'dip_to_black' ? '#000' : '#fff'
      ctx.globalAlpha = mid
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 1
      break
    }
    case 'wipe_left': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      ctx.fillRect(0, 0, w * progress, h)
      break
    }
    case 'wipe_right': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      ctx.fillRect(w * (1 - progress), 0, w * progress, h)
      break
    }
    case 'gl_cube':
    case 'gl_doorway': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      const size = Math.min(w, h) * 0.4 * (0.3 + progress * 0.7)
      ctx.fillStyle = colorB
      ctx.fillRect(w / 2 - size, h / 2 - size, size * 2, size * 2)
      break
    }
    case 'gl_pixelize':
    case 'gl_hexagonalize': {
      const bs = Math.max(2, Math.round((1 - progress) * 12 + 2))
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      for (let x = 0; x < w; x += bs) {
        for (let y = 0; y < h; y += bs) {
          if ((x + y) % (bs * 2) < bs * progress * 2) ctx.fillRect(x, y, bs, bs)
        }
      }
      break
    }
    case 'gl_Swirl': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.save()
      ctx.translate(w / 2, h / 2)
      ctx.rotate(progress * Math.PI * 2)
      ctx.fillStyle = colorB
      ctx.beginPath()
      ctx.arc(0, 0, Math.sqrt(w * w + h * h) * progress * 0.6, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
      break
    }
    case 'gl_ripple': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      const rings = 3
      for (let r = rings; r >= 0; r--) {
        const rProgress = Math.max(0, Math.min(1, progress * (rings + 1) - r))
        ctx.globalAlpha = rProgress * 0.7
        ctx.beginPath()
        ctx.arc(w / 2, h / 2, Math.sqrt(w * w + h * h) * (r + 1) / (rings + 1) * rProgress, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
      break
    }
    case 'gl_morph': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      ctx.beginPath()
      const cx = w / 2
      const cy = h / 2
      const maxR = Math.sqrt(w * w + h * h) * 0.6
      const pts = 6
      for (let i = 0; i <= pts; i++) {
        const angle = (i / pts) * Math.PI * 2
        const r = maxR * progress * (0.7 + 0.3 * Math.sin(angle * 3 + progress * 4))
        const px = cx + Math.cos(angle) * r
        const py = cy + Math.sin(angle) * r
        if (i === 0) ctx.moveTo(px, py)
        else ctx.lineTo(px, py)
      }
      ctx.closePath()
      ctx.fill()
      break
    }
    case 'gl_WaterDrop': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      ctx.beginPath()
      ctx.arc(w / 2, h * 0.3, Math.max(w, h) * progress * 0.4, 0, Math.PI * 2)
      ctx.fill()
      if (progress > 0.3) {
        ctx.globalAlpha = (progress - 0.3) / 0.7
        ctx.fillRect(0, 0, w, h)
        ctx.globalAlpha = 1
      }
      break
    }
    case 'gl_perlin': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      const step = 4
      for (let x = 0; x < w; x += step) {
        for (let y = 0; y < h; y += step) {
          const noise = Math.sin(x * 0.3 + progress * 5) * Math.cos(y * 0.3 + progress * 3) * 0.5 + 0.5
          if (noise < progress) ctx.fillRect(x, y, step, step)
        }
      }
      break
    }
    case 'gl_Radial':
    case 'gl_angular': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      ctx.beginPath()
      ctx.moveTo(w / 2, h / 2)
      ctx.arc(w / 2, h / 2, Math.max(w, h), -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress)
      ctx.closePath()
      ctx.fill()
      break
    }
    case 'gl_BowTieVertical': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      const bw = w * progress
      ctx.fillRect(0, 0, bw / 2, h)
      ctx.fillRect(w - bw / 2, 0, bw / 2, h)
      break
    }
    case 'gl_BowTieHorizontal': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      const bh = h * progress
      ctx.fillRect(0, 0, w, bh / 2)
      ctx.fillRect(0, h - bh / 2, w, bh / 2)
      break
    }
    case 'gl_GlitchMemories': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      for (let i = 0; i < 5; i++) {
        const sliceH = h / 5
        const offset = Math.sin(progress * Math.PI + i) * w * 0.3 * progress
        ctx.fillStyle = i % 2 === 0 ? colorB : accent
        ctx.fillRect(offset, i * sliceH, w * progress, sliceH)
      }
      break
    }
    case 'gl_burn': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      const grad = ctx.createLinearGradient(0, h * (1 - progress), 0, h)
      grad.addColorStop(0, 'transparent')
      grad.addColorStop(0.3, isActive ? CANVAS_BURN_ACTIVE : CANVAS_BURN_INACTIVE)
      grad.addColorStop(1, colorB)
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, w, h)
      break
    }
    case 'gl_kaleidoscope': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.save()
      ctx.translate(w / 2, h / 2)
      const segments = 6
      for (let s = 0; s < segments; s++) {
        ctx.rotate((Math.PI * 2) / segments)
        ctx.fillStyle = s % 2 === 0 ? colorB : accent
        ctx.globalAlpha = progress
        ctx.fillRect(0, -2, w * 0.4 * progress, 4)
      }
      ctx.restore()
      ctx.globalAlpha = 1
      break
    }
    case 'gl_flyeye': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      const dotSize = 3
      const spacing = 6
      for (let x = 0; x < w; x += spacing) {
        for (let y = 0; y < h; y += spacing) {
          const dist = Math.sqrt(Math.pow(x - w / 2, 2) + Math.pow(y - h / 2, 2))
          const maxDist = Math.sqrt(w * w + h * h) / 2
          if (dist / maxDist < progress) {
            ctx.beginPath()
            ctx.arc(x + spacing / 2, y + spacing / 2, dotSize * progress, 0, Math.PI * 2)
            ctx.fill()
          }
        }
      }
      break
    }
    case 'gl_colorphase': {
      const r = Math.sin(progress * Math.PI * 2) * 127 + 128
      const g = Math.sin(progress * Math.PI * 2 + 2) * 127 + 128
      const b = Math.sin(progress * Math.PI * 2 + 4) * 127 + 128
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = progress
      ctx.fillStyle = isActive ? `rgb(${r},${g * 0.5},${b})` : `rgb(${r * 0.5},${g * 0.5},${b * 0.5})`
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 1
      break
    }
    case 'gl_CircleCrop': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.save()
      ctx.beginPath()
      ctx.arc(w / 2, h / 2, Math.max(w, h) * progress * 0.6, 0, Math.PI * 2)
      ctx.clip()
      ctx.fillStyle = colorB
      ctx.fillRect(0, 0, w, h)
      ctx.restore()
      break
    }
    case 'gl_directionalwipe': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      ctx.beginPath()
      ctx.moveTo(w * progress - w * 0.2, 0)
      ctx.lineTo(w * progress + w * 0.2, 0)
      ctx.lineTo(w * progress, h)
      ctx.lineTo(w * progress - w * 0.4, h)
      ctx.fill()
      ctx.fillRect(0, 0, Math.max(0, w * progress - w * 0.2), h)
      break
    }
    case 'gl_windowslice':
    case 'gl_windowblinds':
    case 'gl_squareswire': {
      const blinds = type === 'gl_squareswire' ? 4 : 6
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      const sliceW = w / blinds
      for (let i = 0; i < blinds; i++) ctx.fillRect(i * sliceW, 0, sliceW * progress, h)
      break
    }
    case 'gl_wind': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      for (let i = 0; i < 8; i++) {
        const sy = (h / 8) * i
        const sw = w * progress + Math.sin(i * 1.5 + progress * 4) * w * 0.15
        ctx.fillStyle = i % 2 === 0 ? colorB : accent
        ctx.fillRect(0, sy, Math.max(0, sw), h / 8)
      }
      break
    }
    case 'gl_fadegrayscale': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      const grayVal = Math.round(128 * (1 - progress))
      ctx.fillStyle = isActive ? `rgb(${grayVal}, ${grayVal}, ${Math.min(255, grayVal + 80)})` : `rgb(${grayVal},${grayVal},${grayVal})`
      ctx.globalAlpha = progress
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = progress > 0.5 ? (progress - 0.5) * 2 : 0
      ctx.fillStyle = colorB
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 1
      break
    }
    case 'gl_luma': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      const lumaStep = 4
      for (let x = 0; x < w; x += lumaStep) {
        for (let y = 0; y < h; y += lumaStep) {
          const luma = (x / w + y / h) / 2
          if (luma < progress) ctx.fillRect(x, y, lumaStep, lumaStep)
        }
      }
      break
    }
    case 'gl_Dreamy': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.filter = 'blur(2px)'
      ctx.globalAlpha = progress
      ctx.fillStyle = colorB
      ctx.fillRect(0, 0, w, h)
      ctx.filter = 'none'
      ctx.globalAlpha = 1
      break
    }
    case 'gl_crosswarp': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.save()
      ctx.translate(w / 2, h / 2)
      const scale = 1 + progress * 0.5
      ctx.scale(scale, scale)
      ctx.translate(-w / 2, -h / 2)
      ctx.globalAlpha = progress
      ctx.fillStyle = colorB
      ctx.fillRect(0, 0, w, h)
      ctx.restore()
      ctx.globalAlpha = 1
      break
    }
    case 'gl_DreamyZoom': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.save()
      ctx.translate(w / 2, h / 2)
      const zoomScale = 1 + (1 - progress) * 0.8
      ctx.scale(zoomScale, zoomScale)
      ctx.translate(-w / 2, -h / 2)
      ctx.globalAlpha = progress
      ctx.fillStyle = colorB
      ctx.fillRect(0, 0, w, h)
      ctx.restore()
      ctx.globalAlpha = 1
      break
    }
    case 'gl_luminance_melt': {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.fillStyle = colorB
      for (let x = 0; x < w; x += 3) {
        const melt = progress * h * (0.5 + 0.5 * Math.sin(x * 0.4 + progress * 3))
        ctx.fillRect(x, 0, 3, melt)
      }
      break
    }
    default: {
      ctx.fillStyle = colorA
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = progress
      ctx.fillStyle = colorB
      ctx.fillRect(0, 0, w, h)
      ctx.globalAlpha = 1
      break
    }
  }
}

const CANVAS_W = 48
const CANVAS_H = 28

export const TransitionPreviewCanvas = memo(function TransitionPreviewCanvas({ type, isActive, width: cw = CANVAS_W, height: ch = CANVAS_H }: { type: string; isActive: boolean; width?: number; height?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animRef = useRef<number>(-1)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const el = canvasRef.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), { threshold: 0.1 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!isVisible) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = cw * dpr
    canvas.height = ch * dpr
    ctx.scale(dpr, dpr)

    const startTime = performance.now()

    const draw = (now: number) => {
      const t = (((now - startTime) % 2400) / 2400)
      const progress = t < 0.5 ? t * 2 : 2 - t * 2
      drawTransitionFrame(ctx, type, progress, cw, ch, isActive)
      animRef.current = requestAnimationFrame(draw)
    }

    animRef.current = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(animRef.current)
  }, [type, isActive, isVisible, cw, ch])

  return (
    <canvas
      ref={canvasRef}
      style={{ width: cw, height: ch }}
      className="rounded-sm"
    />
  )
})

interface TransitionPickerProps {
  clipId: string
  currentTransition?: { type: string; duration: number } | null
  onClose: () => void
  clipTitle?: string
}

function formatDuration(d: number): string {
  return d % 1 === 0 ? d + 's' : d.toFixed(1) + 's'
}

export function TransitionPicker({ clipId, currentTransition, onClose, clipTitle }: TransitionPickerProps) {
  const [selectedType, setSelectedType] = useState<string | null>(currentTransition?.type ?? null)
  const [duration, setDuration] = useState(currentTransition?.duration ?? 0.5)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [isPending, setIsPending] = useState(false)
  const updateTransition = useMutation(api.workflows.updateClipTransition)
  const panelRef = useRef<HTMLDivElement>(null)
  const durationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const selectedTypeRef = useRef<string | null>(selectedType)

  useEffect(() => {
    selectedTypeRef.current = selectedType
  }, [selectedType])

  const handleSelect = useCallback(async (type: string) => {
    if (isPending) return
    setSelectedType(type)
    setIsPending(true)
    try {
      await updateTransition({
        clipId: clipId as Id<"workflowClips">,
        type,
        duration,
      })
    } finally {
      setIsPending(false)
    }
  }, [clipId, duration, updateTransition, isPending])

  const handleDurationChange = useCallback((newDuration: number) => {
    setDuration(newDuration)
    if (durationTimerRef.current) clearTimeout(durationTimerRef.current)
    durationTimerRef.current = setTimeout(() => {
      const currentType = selectedTypeRef.current
      if (currentType) {
        updateTransition({
          clipId: clipId as Id<"workflowClips">,
          type: currentType,
          duration: newDuration,
        })
      }
    }, 300)
  }, [clipId, updateTransition])

  const handleRemove = useCallback(async () => {
    if (isPending) return
    setSelectedType(null)
    setIsPending(true)
    try {
      await updateTransition({
        clipId: clipId as Id<"workflowClips">,
        type: 'cut',
      })
      onClose()
    } finally {
      setIsPending(false)
    }
  }, [clipId, updateTransition, onClose, isPending])

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [onClose])

  useEffect(() => {
    return () => {
      if (durationTimerRef.current) clearTimeout(durationTimerRef.current)
    }
  }, [])

  return (
    <div
      ref={panelRef}
      className="relative z-10 w-[320px] bg-surface border border-surface-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150"
      style={{ maxHeight: '70vh' }}
      role="dialog"
      aria-modal="true"
      aria-label="Transition picker"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-surface-border">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-surface-foreground tracking-wide uppercase">Transition</span>
          {clipTitle && (
            <span className="text-[10px] text-surface-muted truncate max-w-[140px]">
              into {clipTitle}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-surface-elevated text-surface-muted hover:text-surface-foreground"
        >
          <X size={14} />
        </button>
      </div>

      <div className="px-3 py-2 border-b border-surface-border space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-surface-muted">Duration</span>
          <span className={cn(
            'text-[11px] font-mono tabular-nums',
            selectedType ? 'text-surface-foreground' : 'text-surface-muted/50'
          )}>{formatDuration(duration)}</span>
        </div>
        <input
          type="range"
          min={0.1}
          max={2.0}
          step={0.1}
          value={duration}
          onChange={(e) => handleDurationChange(parseFloat(e.target.value))}
          disabled={!selectedType}
          className={cn(
            'w-full h-1 appearance-none rounded-full [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full',
            selectedType
              ? 'bg-surface-border cursor-pointer [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer'
              : 'bg-surface-elevated cursor-not-allowed [&::-webkit-slider-thumb]:bg-surface-muted'
          )}
        />
        {selectedType && (
          <button
            onClick={handleRemove}
            disabled={isPending}
            className="flex items-center gap-1.5 text-[11px] text-red-400 hover:text-red-300 py-0.5 disabled:opacity-50"
          >
            <Trash2 size={11} />
            Remove transition
          </button>
        )}
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 100px)' }}>
        {/* None / Cut tile */}
        <div className="px-2 pt-2 pb-1">
          <button
            onClick={handleRemove}
            disabled={isPending}
            aria-label="None (Cut)"
            aria-pressed={!selectedType}
            className={cn(
              'w-full flex items-center gap-2 p-2 rounded-md border transition-all text-left',
              !selectedType
                ? 'border-surface-muted bg-surface-elevated/60'
                : 'border-dashed border-surface-border hover:border-surface-muted hover:bg-surface-elevated/40',
              'disabled:opacity-50'
            )}
          >
            <div className="w-12 h-7 rounded-sm bg-surface-elevated border border-surface-border flex items-center justify-center">
              <span className="text-[9px] text-surface-muted font-mono">CUT</span>
            </div>
            <div>
              <span className={cn(
                'text-[11px] font-medium',
                !selectedType ? 'text-surface-foreground' : 'text-surface-muted'
              )}>None</span>
              <span className="text-[9px] text-surface-muted/50 ml-1.5">Hard cut</span>
            </div>
          </button>
        </div>

        {TRANSITION_CATEGORIES.map((category) => {
          const isExpanded = expandedCategory === null || expandedCategory === category.label
          const hasActive = category.transitions.some(t => t.type === selectedType)

          return (
            <div key={category.label}>
              <button
                onClick={() => setExpandedCategory(prev => prev === category.label ? null : category.label)}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold tracking-wider uppercase',
                  hasActive ? 'text-primary' : 'text-surface-muted',
                  'hover:bg-surface-elevated/50'
                )}
              >
                {category.label}
                <ChevronRight size={12} className={cn(
                  'text-surface-muted/50 transition-transform duration-150',
                  isExpanded && 'rotate-90'
                )} />
              </button>
              {isExpanded && (
                <div className="grid grid-cols-3 gap-1 px-2 pb-2">
                  {category.transitions.map((t) => {
                    const isActive = selectedType === t.type
                    return (
                      <button
                        key={t.type}
                        onClick={() => handleSelect(t.type)}
                        disabled={isPending}
                        aria-label={t.name}
                        aria-pressed={isActive}
                        className={cn(
                          'flex flex-col items-center gap-1 p-1.5 rounded-md border transition-all',
                          isActive
                            ? 'border-primary bg-primary/10 ring-2 ring-primary ring-offset-1 ring-offset-surface'
                            : 'border-transparent hover:border-surface-border hover:bg-surface-elevated/60',
                          'disabled:opacity-50'
                        )}
                      >
                        <TransitionPreviewCanvas type={t.type} isActive={isActive} />
                        <span className={cn(
                          'text-[10px] leading-tight text-center',
                          isActive ? 'text-primary font-medium' : 'text-surface-muted'
                        )}>
                          {t.name}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export function TransitionIndicator({
  transition,
  onClick,
  size = 'sm',
}: {
  transition?: { type: string; duration: number } | null
  onClick: () => void
  size?: 'sm' | 'md'
}) {
  const hasTransition = transition && transition.type !== 'cut'
  const label = hasTransition ? getTransitionLabel(transition.type) : null

  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={cn(
        'flex items-center justify-center shrink-0 transition-all rounded-full',
        size === 'sm' ? 'w-5 h-5' : 'w-6 h-6',
        hasTransition
          ? 'bg-primary/20 border border-primary/40 text-primary hover:bg-primary/30 hover:border-primary/60'
          : 'border border-dashed border-surface-muted text-surface-muted hover:border-surface-foreground hover:text-surface-foreground'
      )}
      title={hasTransition ? `${label} (${transition!.duration}s)` : 'Add transition'}
    >
      {hasTransition ? (
        <span className="text-[7px] font-bold leading-none">{label}</span>
      ) : (
        <svg width={size === 'sm' ? 8 : 10} height={size === 'sm' ? 8 : 10} viewBox="0 0 10 10" fill="none">
          <path d="M5 2V8M2 5H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      )}
    </button>
  )
}

function getTransitionLabel(type: string): string {
  const labels: Record<string, string> = {
    crossfade: 'XF',
    dip_to_black: 'DB',
    dip_to_white: 'DW',
    wipe_left: 'WL',
    wipe_right: 'WR',
  }
  if (labels[type]) return labels[type]
  const name = type.replace(/^gl_/, '')
  return name.slice(0, 2).toUpperCase()
}

export { TRANSITION_CATEGORIES }
export type { TransitionType }
